// Expenses — docs/MONEY_TAXES.md Phase 5.
//
// "Enter, attach a receipt to, and categorise expenses; they join Money in &
// out as a second 'out' line, and the Net line becomes a real profit-and-loss."
//
//   GET    /api/office/expenses?from=&to=[&category=]   financials.read
//   POST   /api/office/expenses                         financials.read + export
//   PUT    /api/office/expenses/:id
//   DELETE /api/office/expenses/:id
//   POST   /api/office/expenses/:id/receipt             (multipart, "receipt")
//   DELETE /api/office/expenses/:id/receipt
//   GET    /api/office/expenses/:id/receipt             financials.read
//
// Every total is the server's, in CENTS (server/utils/expenses.js says what
// each one is). The one sum this screen does is none: the category list and
// the totals come back with the rows. What it decides is what a person typed:
// dollars become whole cents or nothing (dollarsToCents, never a silent 0),
// and the category and payment lists are the server's own, sent with the rows.
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable, FwButton, FwField, FwInput, FwSelect, FwTextarea, Notice, SectionLabel, Sheet, Stat } from '../../../ui/fw';
import { useCan } from '../../../hooks/usePermission';
import { get, post, put, del } from '../../shared/http';
import { downloadBlob } from '../../shared/download';
import { fmtDate, fmtDateRange, todayIso } from '../shared/format';
import { useAction, FormError, FormRow, SheetFooter, ConfirmSheet, downloadText, csvLine } from '../records/OfficePage';
import { PageHeader, Refused, LoadFailed, Loading } from '../shared/Page';
import { PERIODS, periodRange } from './MoneyInOut';
import { money, dollarsToCents } from './SalesTax';
import './Expenses.css';

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const RECEIPT_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';

/** Cents → the "12.34" a form field shows; blank for nothing recorded. */
const centsToField = (c) => (c == null ? '' : (c / 100).toFixed(2));

/** Every row, then the split — what the accountant is sent. */
export function expensesCsv(list) {
  const d = (c) => (c == null ? '' : (c / 100).toFixed(2));
  const rows = [
    ['Expenses', `${list.from} to ${list.to}`],
    [],
    ['Date', 'Paid to', 'Category', 'What for', 'Amount', 'Sales tax', 'Paid by', 'Job', 'Receipt', 'Notes'],
    ...list.expenses.map((e) => [e.spent_on, e.vendor || '', e.category_label, e.description || '', d(e.amount_cents),
      d(e.tax_cents), e.method_label || '', e.job_title || '', e.has_receipt ? 'yes' : 'no', e.notes || '']),
    [],
    ['Category', 'Count', 'Total'],
    ...list.by_category.map((c) => [c.label, c.count, d(c.total_cents)]),
    ['All expenses', list.count, d(list.total_cents)],
  ];
  return rows.map((r) => csvLine(r)).join('\n');
}

/**
 * The body a form sends. `amount` and `tax` are what the person typed;
 * the answer is { error } or { body }, never a guessed figure.
 */
export function expenseBody(form) {
  const amount = dollarsToCents(form.amount);
  if (amount == null || amount === 0) return { error: 'The amount must be an amount more than 0, like 45.99.' };
  let tax = null;
  if (String(form.tax || '').trim() !== '') {
    tax = dollarsToCents(form.tax);
    if (tax == null) return { error: 'The sales tax must be an amount, like 2.75, or left blank.' };
    if (tax > amount) return { error: 'The sales tax is part of the amount, so it cannot be more than the amount.' };
  }
  if (!form.category) return { error: 'Choose a category.' };
  if (!String(form.vendor || '').trim() && !String(form.description || '').trim()) {
    return { error: 'Say who it was paid to, or what it was for.' };
  }
  return {
    body: {
      spent_on: form.spent_on,
      amount_cents: amount,
      tax_cents: tax,
      vendor: form.vendor,
      category: form.category,
      description: form.description,
      method: form.method || null,
      job_id: form.job_id ? Number(form.job_id) : null,
      notes: form.notes,
    },
  };
}

export default function Expenses() {
  const canRead = useCan('financials', 'read');
  const canWrite = useCan('financials', 'export');
  const [params, setParams] = useSearchParams();
  const today = todayIso();
  const [editing, setEditing] = useState(null); // null | 'new' | expense

  const rawPeriod = params.get('period') || 'this-month';
  const period = PERIODS.some(([k]) => k === rawPeriod) ? rawPeriod : 'this-month';
  const named = periodRange(period === 'custom' ? 'this-month' : period, today);
  const from = period === 'custom' && DAY.test(params.get('from') || '') ? params.get('from') : named.from;
  const to = period === 'custom' && DAY.test(params.get('to') || '') ? params.get('to') : named.to;
  const category = params.get('category') || '';
  const backwards = from > to;

  const setParam = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    setParams(next, { replace: true });
  };
  const choosePeriod = (key) => {
    if (key === 'custom') setParam({ period: 'custom', from, to });
    else setParam({ period: key === 'this-month' ? '' : key, from: '', to: '' });
  };

  const q = useQuery({
    queryKey: ['office', 'expenses', from, to, category],
    queryFn: () => get(`/office/expenses?from=${from}&to=${to}${category ? `&category=${category}` : ''}`),
    enabled: canRead && !backwards,
  });
  const s = q.data;

  const columns = useMemo(() => [
    { key: 'vendor', label: 'Paid to', role: 'title', render: (e) => <strong>{e.vendor || e.description}</strong> },
    { key: 'category', label: 'Category', role: 'subtitle', render: (e) => e.category_label },
    { key: 'spent_on', label: 'Date', role: 'fact', render: (e) => fmtDate(e.spent_on) },
    { key: 'receipt', label: 'Receipt', role: 'fact', render: (e) => (e.has_receipt ? 'Attached' : 'None') },
    { key: 'description', label: 'What for', role: 'hidden', render: (e) => (e.vendor ? e.description || '—' : '—') },
    { key: 'job', label: 'Job', role: 'hidden', render: (e) => e.job_title || '—' },
    { key: 'amount', label: 'Amount', align: 'right', role: 'badge', render: (e) => <strong>{money(e.amount_cents)}</strong> },
  ], []);
  const categoryColumns = useMemo(() => [
    { key: 'label', label: 'Category', role: 'title' },
    { key: 'count', label: 'Receipts', align: 'right', role: 'fact' },
    { key: 'total', label: 'Total', align: 'right', role: 'badge', render: (c) => <strong>{money(c.total_cents)}</strong> },
  ], []);

  if (!canRead) return <Refused what="expenses" />;

  const exportCsv = () => downloadText(expensesCsv(s), `expenses-${from}-to-${to}.csv`);
  const categories = (s && s.categories) || [];

  return (
    <div className="of-exp" data-screen="office-Expenses">
      <PageHeader
        title="Expenses"
        meta={fmtDateRange(from, to)}
        // On a phone the header keeps ONE action and folds the rest into
        // More. Without this it kept Export CSV and buried Add — measured by
        // ui-check's expenses scenario at 390px, 2026-09-28.
        primaryAction="add-expense"
        actions={(
          <>
            {canWrite && s ? <FwButton variant="ghost" icon="download" onClick={exportCsv} data-action="expenses-export">Export CSV</FwButton> : null}
            {/* Not while the list failed to load: the sheet's category list comes
                with the rows, so it would open with nothing to choose (review of #726). */}
            {canWrite && s ? <FwButton icon="plus" onClick={() => setEditing('new')} data-action="add-expense">Add expense</FwButton> : null}
          </>
        )}
      />
      <div className="of-filters">
        <FwField label="Period" htmlFor="of-exp-period">
          <FwSelect id="of-exp-period" value={period} onChange={(e) => choosePeriod(e.target.value)}>
            {PERIODS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </FwSelect>
        </FwField>
        {period === 'custom' ? (
          <>
            <FwField label="From" htmlFor="of-exp-from"><FwInput id="of-exp-from" type="date" value={from} onChange={(e) => setParam({ from: e.target.value })} /></FwField>
            <FwField label="To" htmlFor="of-exp-to"><FwInput id="of-exp-to" type="date" value={to} onChange={(e) => setParam({ to: e.target.value })} /></FwField>
          </>
        ) : null}
        <FwField label="Category" htmlFor="of-exp-category">
          <FwSelect id="of-exp-category" value={category} onChange={(e) => setParam({ category: e.target.value })}>
            <option value="">Every category</option>
            {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </FwSelect>
        </FwField>
      </div>
      {backwards ? <Notice tone="warn">The start date is after the end date.</Notice> : null}
      {q.isPending && !backwards ? <Loading lines={6} /> : null}
      {q.isError ? <LoadFailed error={q.error} onRetry={() => q.refetch()} what="the expenses" /> : null}

      {s ? (
        <>
          <div className="of-stats__row">
            <Stat data-stat="spent" label="Spent" value={money(s.total_cents)}
              hint={`${s.count} expense${s.count === 1 ? '' : 's'}${category ? ` in ${(categories.find((c) => c.key === category) || {}).label || category}` : ''}, sales tax included`} />
            <Stat data-stat="tax" label="Sales tax on receipts" value={money(s.tax_cents)}
              hint="The part of what was spent that receipts show as tax, where it was recorded" />
          </div>

          {!category && s.by_category.length ? (
            <section className="of-exp__section" data-section="categories">
              <SectionLabel>By category</SectionLabel>
              <DataTable columns={categoryColumns} rows={s.by_category.map((c) => ({ ...c, id: c.category }))} caption="Expenses by category" dense />
            </section>
          ) : null}

          <section className="of-exp__section" data-section="expenses">
            <SectionLabel>Every expense</SectionLabel>
            <DataTable columns={columns} rows={s.expenses} caption="Expenses in this period"
              onRowClick={(e) => setEditing(e)}
              empty={category ? 'No expenses in this category in this period.' : 'No expenses are recorded in this period.'} />
          </section>
        </>
      ) : null}

      {editing ? (
        <ExpenseSheet
          key={editing === 'new' ? 'new' : `e${editing.id}`}
          expense={editing === 'new' ? null : editing}
          categories={categories}
          methods={(s && s.methods) || []}
          canWrite={canWrite}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

/** Add, change, attach a receipt to, or delete one expense. */
function ExpenseSheet({ expense, categories, methods, canWrite, onClose }) {
  const qc = useQueryClient();
  const today = todayIso();
  const [form, setForm] = useState(() => ({
    spent_on: expense ? expense.spent_on : today,
    amount: expense ? centsToField(expense.amount_cents) : '',
    tax: expense ? centsToField(expense.tax_cents) : '',
    vendor: expense ? expense.vendor || '' : '',
    category: expense ? expense.category : '',
    description: expense ? expense.description || '' : '',
    method: expense ? expense.method || '' : '',
    job_id: expense && expense.job_id ? String(expense.job_id) : '',
    notes: expense ? expense.notes || '' : '',
  }));
  const [file, setFile] = useState(null);
  // The row this sheet has already made. After a refused receipt the sheet
  // stays open, and a second Save must change THIS row, never add another:
  // it did add another, and every total counted the expense twice (review
  // of #726).
  const [created, setCreated] = useState(null);
  const current = expense || created;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { busy, error, run, setError } = useAction();
  const set = (k) => (e) => setForm((v) => ({ ...v, [k]: e.target.value }));
  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: ['office', 'expenses'] }),
    qc.invalidateQueries({ queryKey: ['office', 'money'] }),
  ]);

  // Jobs, for the optional "which job was this for". A failure hides the
  // picker and says so rather than blocking the expense.
  const jobs = useQuery({
    queryKey: ['office', 'expenses', 'jobs'],
    queryFn: () => get('/jobs'),
    enabled: canWrite,
    staleTime: 60000,
  });
  const jobList = Array.isArray(jobs.data) ? jobs.data : (jobs.data && Array.isArray(jobs.data.jobs) ? jobs.data.jobs : []);
  const jobOptions = jobList
    .filter((j) => !['archived', 'cancelled'].includes(j.status) || String(j.id) === form.job_id)
    .sort((a, b) => String(a.title).localeCompare(String(b.title)));

  const save = async (e) => {
    e.preventDefault();
    const built = expenseBody(form);
    if (built.error) { setError(built.error); return; }
    let saved = null;
    const ok = await run(async () => {
      const r = current
        ? await put(`/office/expenses/${current.id}`, built.body)
        : await post('/office/expenses', built.body);
      saved = r && r.expense;
    }, 'Could not save the expense.');
    if (!ok) return;
    if (!current && saved) setCreated(saved);
    if (file && saved) {
      const fd = new FormData();
      fd.append('receipt', file);
      const attached = await run(() => post(`/office/expenses/${saved.id}/receipt`, fd), 'The receipt could not be attached.');
      if (!attached) { await refresh(); return; }
    }
    await refresh();
    onClose();
  };
  const removeReceipt = async () => {
    const ok = await run(() => del(`/office/expenses/${expense.id}/receipt`), 'Could not remove the receipt.');
    if (ok) { await refresh(); onClose(); }
  };
  const remove = async () => {
    const ok = await run(() => del(`/office/expenses/${expense.id}`), 'Could not delete the expense.');
    if (ok) { setConfirmDelete(false); await refresh(); onClose(); }
  };
  const openReceipt = () => run(() => downloadBlob(`/office/expenses/${expense.id}/receipt`, expense.receipt_name || 'receipt'), 'Could not open the receipt.');

  const title = current ? (current.vendor || current.description || 'Expense') : 'Add expense';
  if (!canWrite) {
    return (
      <Sheet open title={title} onClose={onClose}>
        <dl className="of-exp__facts" data-view="expense">
          <div><dt>Date</dt><dd>{fmtDate(expense.spent_on)}</dd></div>
          <div><dt>Amount</dt><dd><strong>{money(expense.amount_cents)}</strong></dd></div>
          <div><dt>Sales tax</dt><dd>{expense.tax_cents == null ? 'Not recorded' : money(expense.tax_cents)}</dd></div>
          <div><dt>Category</dt><dd>{expense.category_label}</dd></div>
          {expense.description ? <div><dt>What for</dt><dd>{expense.description}</dd></div> : null}
          {expense.method_label ? <div><dt>Paid by</dt><dd>{expense.method_label}</dd></div> : null}
          {expense.job_title ? <div><dt>Job</dt><dd>{expense.job_title}</dd></div> : null}
        </dl>
        <FormError error={error} />
        {expense.has_receipt ? <FwButton variant="ghost" icon="download" onClick={openReceipt} loading={busy} data-action="open-receipt">Open the receipt</FwButton> : null}
      </Sheet>
    );
  }

  return (
    <Sheet open title={title} onClose={onClose} footer={<SheetFooter onCancel={onClose} busy={busy} submitLabel={current ? 'Save' : 'Add expense'} formId="of-exp-form" />}>
      <form id="of-exp-form" className="or-form of-exp__form" onSubmit={save} data-form="expense">
        <FormError error={error} />
        {created ? (
          <Notice tone="info" data-note="saved-without-receipt">
            The expense is saved. Only the receipt is missing: choose another file and press Save to attach it.
          </Notice>
        ) : null}
        <FormRow>
          <FwField label="Date on the receipt" htmlFor="of-exp-date" required>
            <FwInput id="of-exp-date" type="date" max={today} value={form.spent_on} onChange={set('spent_on')} required />
          </FwField>
          <FwField label="Amount" htmlFor="of-exp-amount" required hint="What was paid, tax included">
            <FwInput id="of-exp-amount" inputMode="decimal" value={form.amount} onChange={set('amount')} required />
          </FwField>
        </FormRow>
        <FormRow>
          <FwField label="Sales tax" htmlFor="of-exp-tax" hint="The part of the amount the receipt shows as tax. Leave blank if it does not say">
            <FwInput id="of-exp-tax" inputMode="decimal" value={form.tax} onChange={set('tax')} />
          </FwField>
          <FwField label="Category" htmlFor="of-exp-cat" required>
            <FwSelect id="of-exp-cat" value={form.category} onChange={set('category')} required>
              <option value="">Choose…</option>
              {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </FwSelect>
          </FwField>
        </FormRow>
        <FormRow>
          <FwField label="Paid to" htmlFor="of-exp-vendor"><FwInput id="of-exp-vendor" value={form.vendor} onChange={set('vendor')} maxLength={120} /></FwField>
          <FwField label="Paid by" htmlFor="of-exp-method">
            <FwSelect id="of-exp-method" value={form.method} onChange={set('method')}>
              <option value="">Not recorded</option>
              {methods.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </FwSelect>
          </FwField>
        </FormRow>
        <FwField label="What it was for" htmlFor="of-exp-desc"><FwInput id="of-exp-desc" value={form.description} onChange={set('description')} maxLength={500} /></FwField>
        {jobs.isError ? (
          <p className="of-muted" data-note="no-jobs">The job list could not be loaded, so this expense cannot be put against a job right now.</p>
        ) : (
          <FwField label="Job" htmlFor="of-exp-job" hint="Materials bought for one job">
            <FwSelect id="of-exp-job" value={form.job_id} onChange={set('job_id')}>
              <option value="">No job</option>
              {jobOptions.map((j) => <option key={j.id} value={String(j.id)}>{j.title}</option>)}
            </FwSelect>
          </FwField>
        )}
        <FwField label="Notes" htmlFor="of-exp-notes"><FwTextarea id="of-exp-notes" rows={2} value={form.notes} onChange={set('notes')} maxLength={2000} /></FwField>

        <SectionLabel>Receipt</SectionLabel>
        {expense && expense.has_receipt ? (
          <div className="of-exp__receipt" data-receipt="attached">
            <span className="of-exp__receipt-name">{expense.receipt_name || 'Receipt'}</span>
            <FwButton variant="ghost" icon="download" onClick={openReceipt} disabled={busy} data-action="open-receipt">Open</FwButton>
            <FwButton variant="ghost" icon="trash" onClick={removeReceipt} disabled={busy} data-action="remove-receipt">Remove</FwButton>
          </div>
        ) : null}
        <FwField label={expense && expense.has_receipt ? 'Replace it with' : 'Photo or PDF'} htmlFor="of-exp-file" hint="Up to 15 MB">
          <input id="of-exp-file" type="file" accept={RECEIPT_ACCEPT} className="of-exp__file" data-field="receipt"
            onChange={(e) => setFile((e.target.files && e.target.files[0]) || null)} />
        </FwField>
        <Notice tone="info" data-note="not-reimbursements">
          A receipt a worker was paid back for on their timesheet is already counted in payroll. Do not enter it here as well, or it is counted twice.
        </Notice>

        {expense ? (
          <div className="of-exp__danger">
            <FwButton variant="ghost" icon="trash" onClick={() => setConfirmDelete(true)} disabled={busy} data-action="delete-expense">Delete this expense</FwButton>
          </div>
        ) : null}
      </form>
      <ConfirmSheet open={confirmDelete} title="Delete this expense?" confirmLabel="Delete expense" busy={busy}
        onConfirm={remove} onClose={() => setConfirmDelete(false)}>
        <p>{`${money(expense ? expense.amount_cents : 0)} on ${expense ? fmtDate(expense.spent_on) : ''} comes off the period's expenses${expense && expense.has_receipt ? ', and its receipt is deleted with it' : ''}.`}</p>
      </ConfirmSheet>
    </Sheet>
  );
}
